/**
 * Fantasy Console 0 - File Tabs Component
 *
 * Horizontal tabs for switching between code files in multi-file carts.
 */

import { useState, useRef, useEffect } from 'react'
import type { CodeFile } from '@fc0/types'
import { CloseIcon } from '../shared/icons'
import styles from './FileTabs.module.css'

interface FileTabsProps {
  files: CodeFile[]
  activeIndex: number
  onSelectFile: (index: number) => void
  onAddFile: () => void
  onRemoveFile: (index: number) => void
  onRenameFile: (index: number, newName: string) => void
  maxFiles: number
  tokenCounts?: number[]
}

export function FileTabs({
  files,
  activeIndex,
  onSelectFile,
  onAddFile,
  onRemoveFile,
  onRenameFile,
  maxFiles,
  tokenCounts,
}: FileTabsProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when editing starts
  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingIndex])

  const startRename = (index: number) => {
    setEditingIndex(index)
    setEditingName(files[index].name)
  }

  const confirmRename = () => {
    if (editingIndex !== null) {
      const trimmed = editingName.trim()
      if (trimmed && trimmed !== files[editingIndex].name) {
        onRenameFile(editingIndex, trimmed)
      }
      setEditingIndex(null)
      setEditingName('')
    }
  }

  const cancelRename = () => {
    setEditingIndex(null)
    setEditingName('')
  }

  const handleTabClick = (index: number, e: React.MouseEvent) => {
    // Only select if not clicking the close button
    if ((e.target as HTMLElement).closest(`.${styles.closeButton}`)) {
      return
    }
    onSelectFile(index)
  }

  const handleClose = (index: number, e: React.MouseEvent) => {
    e.stopPropagation()
    onRemoveFile(index)
  }

  return (
    <div className={styles.fileTabs}>
      <div className={styles.tabsList}>
        {files.map((file, index) => (
          <div
            key={index}
            className={`${styles.fileTab} ${index === activeIndex ? styles.active : ''}`}
            onClick={(e) => handleTabClick(index, e)}
          >
            {editingIndex === index ? (
              <input
                ref={inputRef}
                type='text'
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={confirmRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    confirmRename()
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    cancelRename()
                  }
                }}
                className={styles.fileNameInput}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <>
                <span
                  className={styles.fileName}
                  onDoubleClick={() => startRename(index)}
                  title={file.name}
                >
                  {file.name}
                </span>

                {tokenCounts && tokenCounts[index] !== undefined && (
                  <span className={styles.tokenCount}>
                    ({tokenCounts[index]})
                  </span>
                )}

                {files.length > 1 && (
                  <button
                    className={styles.closeButton}
                    onClick={(e) => handleClose(index, e)}
                    title='Close file'
                    aria-label={`Close ${file.name}`}
                  >
                    <CloseIcon size={14} />
                  </button>
                )}
              </>
            )}
          </div>
        ))}

        {files.length < maxFiles && (
          <button
            className={styles.addButton}
            onClick={onAddFile}
            title='Add new file'
            aria-label='Add new file'
          >
            <PlusIcon />
          </button>
        )}
      </div>
    </div>
  )
}

function PlusIcon() {
  return (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='currentColor'>
      <path
        d='M6 1V11M1 6H11'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  )
}
